import React, { FC, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Spinner,
  SpinnerSize,
  Intrinsic,
  Button,
  ButtonSize,
  ButtonAppearance,
  Box,
  Text,
  Icon,
  IconSize,
} from '@bechtel/gds';
import { FileUploadProps, FileUploadInputProps, FileUploadPreviewImgProps, FileUploadError } from './types';
import { useFileUpload } from './hooks';
import { FileUploadContainer, DeletePhotoStyled, PhotoContainerStyled } from './styled';
import { Trans } from '@lingui/macro';
import { forEach, join } from 'lodash';
import { FileUploadCustomError } from './error';

const noop = () => undefined;

export const FileUpload: FC<FileUploadProps> = ({
  children,
  onSuccess,
  onError = noop,
  maxFiles = 1,
  filesCount = 0,
  maxFileSizeMB,
  acceptFileTypes,
}) => {
  const [fileUploadError, setFileUploadError] = useState<Error | FileUploadError | undefined>();
  const onFileUploadError = (error?: Error | FileUploadError) => {
    if (!error) return;
    setFileUploadError(error);
    onError(error);
  };
  const [onDrop, { loading, files, setFiles }] = useFileUpload(
    onSuccess,
    onFileUploadError,
    maxFileSizeMB,
    acceptFileTypes
  );
  const { getRootProps, getInputProps } = useDropzone({
    accept: acceptFileTypes ? acceptFileTypes : '',
    disabled: filesCount >= maxFiles,
    multiple: false,
    onDrop,
  });

  return (
    <>
      {fileUploadError && (
        <FileUploadCustomError
          maxFileSizeMB={maxFileSizeMB}
          error={fileUploadError}
          close={() => {
            setFileUploadError(undefined);
          }}
          acceptFileTypes={acceptFileTypes}
        />
      )}
      {children({
        getRootProps,
        getInputProps,
        loading,
        files,
        setFiles,
        acceptFileTypes,
      })}
    </>
  );
};

export const FileUploadInput: FC<FileUploadInputProps> = ({
  getRootProps,
  getInputProps,
  loading,
  acceptFileTypes,
}) => {
  const fileTypes: string[] = [];
  if (acceptFileTypes && acceptFileTypes.length > 0) {
    if (Array.isArray(acceptFileTypes)) {
      forEach(acceptFileTypes, (item) => {
        fileTypes.push(item.split('/')[1]);
      });
    } else {
      const spliteAcceptFileTypes = acceptFileTypes.split(',');
      forEach(spliteAcceptFileTypes, (item) => {
        fileTypes.push(item.split('/')[1]);
      });
    }
  }
  return (
    <FileUploadContainer {...getRootProps()}>
      <input {...getInputProps()} />
      {loading && <Spinner size={SpinnerSize.LARGE} />}
      {!loading && (
        <Box flexDirection={{ sm: 'column' }} alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }}>
          <Box mb={{ sm: '400' }}>
            <Icon icon="cloud-upload" size={IconSize.LARGE} />
          </Box>
          <Text size="400">
            <Trans>Drag and drop or choose a file</Trans>
          </Text>
          {fileTypes.length > 0 && (
            <Box mt={{ sm: '200' }}>
              <Text size="300">
                <Trans>Only following file types are supported:</Trans> &nbsp;
                {join(fileTypes, ', ')}
              </Text>
            </Box>
          )}
        </Box>
      )}
    </FileUploadContainer>
  );
};

export const FileUploadPreviewImg: FC<FileUploadPreviewImgProps> = ({ file, onDelete }) => {
  return (
    <PhotoContainerStyled key={file.preview}>
      <Intrinsic ratio={{ sm: '1:1', md: '4:3', lg: '16:9' }}>
        <img src={file.preview} />
      </Intrinsic>
      <DeletePhotoStyled>
        <Button
          appearance={ButtonAppearance.SECONDARY}
          size={ButtonSize.SMALL}
          onClick={() => {
            if (!onDelete) return;
            onDelete(file);
          }}
        >
          <Box mr={{ sm: '300' }}>
            <Icon icon="trash-alt" fillColor="red.800" />
          </Box>
          <Trans>Delete</Trans>
        </Button>
      </DeletePhotoStyled>
    </PhotoContainerStyled>
  );
};
